import { Trans, useLingui } from '@lingui/react/macro';

import Badge from '@/design_system/Badge';
import { InputSelect } from '@/design_system/InputSelect/InputSelect';
import Stack from '@/design_system/Stack';
import Toggle from '@/design_system/Toggle';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconAfterSales from '@/icons/AfterSales.svg';
import {
  EXCEPTIONAL_BRAND_COVERAGE_REASONS,
  TEMPORARY_ID_PREFIX,
  useUpdateDefect,
} from '@/models/article';
import { type ArticleDefectWithRelations, ArticleWithRelations } from '@/models/request';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { RequestArticleContextData } from '@/routes/Requests/contexts/interfaces';

import { getEditedFields } from './getEditedFields';
import { LinkedOriginalRequestReference } from './LinkedOriginalRequestReference';
import { OriginalRequestSearchSelect } from './OriginalRequestSearchSelect';

export const shouldShowExceptionalCoverageToggle = ({
  view,
  defect,
  article,
}: {
  view: RequestArticleContextData['view'];
  defect?: ArticleDefectWithRelations;
  article: ArticleWithRelations;
}): boolean => {
  const contextEnablesExceptionalCoverageToggle =
    !!defect &&
    view.defects.shown &&
    view.defects.fields.brandResponsibility.enabled &&
    !view.defects.fields.brandResponsibility.toggle.shown &&
    view.defects.fields.brandResponsibility.exceptionalCoverage.enabled;

  if (!contextEnablesExceptionalCoverageToggle) {
    return false;
  }

  // The default brand responsibility only applies when the article is under warranty
  const isCoveredByDefaultBrandResponsibility =
    !!article.activeWarrantyType && defect.defaultBrandResponsibility;

  return !isCoveredByDefaultBrandResponsibility;
};

export const getExceptionalCoverageRequestLabelType = (article: ArticleWithRelations) => {
  // This logic should stay aligned with the `shouldShowExceptionalBrandCoverageRequested` on the API
  const latestServiceChoiceStepIndex = (article.steps ?? []).findLastIndex(
    (step) => step.step === 'service-choice' && ['done', 'in-progress'].includes(step.status)
  );

  const stepsFollowingServiceChoice = article.steps?.slice(latestServiceChoiceStepIndex + 1);

  const articleHasHadAnalysisOrProcessingStepSinceServiceChoice = stepsFollowingServiceChoice?.some(
    (step) =>
      ['done', 'in-progress'].includes(step.status) &&
      ['analysis', 'processing'].includes(step.step)
  );

  return articleHasHadAnalysisOrProcessingStepSinceServiceChoice ? 'granted' : 'requested';
};

export const DefectExceptionalCoverageMobile = ({
  defect,
  previousDefect,
}: {
  defect?: ArticleDefectWithRelations;
  previousDefect?: ArticleDefectWithRelations;
}) => {
  const { t, i18n } = useLingui();
  const { article } = useArticleContext();

  const labelType = getExceptionalCoverageRequestLabelType(article);
  const label =
    labelType === 'requested'
      ? t({
          id: 'defect-card.exceptional-coverage.requested',
          message: 'Exceptional brand coverage requested',
        })
      : t({
          id: 'defect-card.exceptional-coverage.covered-by-brand',
          message: 'Covered by brand',
        });

  const editedFields = getEditedFields({ defect, previousDefect });

  const defectOrPreviousDefect = defect ?? previousDefect;
  const exceptionalCoverageReason = EXCEPTIONAL_BRAND_COVERAGE_REASONS.find(
    ({ id }) => id === defectOrPreviousDefect?.exceptionalBrandResponsibilityCoverageReason
  );

  return (
    <Badge
      hasBorder
      color="neutral"
      borderRadius="soft"
      size="large"
      style={
        editedFields.isExceptionalBrandResponsibilityCoverageRequestedEdited ||
        editedFields.isExceptionalBrandResponsibilityCoverageReasonEdited
          ? { backgroundColor: 'var(--color-tertiary-100)' }
          : undefined
      }
    >
      <IconAfterSales />
      {label}
      {!!exceptionalCoverageReason && <> ({i18n._(exceptionalCoverageReason.text)})</>}
    </Badge>
  );
};

export const DefectExceptionalCoverageToggle = ({
  defect,
  previousDefect,
}: {
  defect?: ArticleDefectWithRelations;
  previousDefect?: ArticleDefectWithRelations;
}) => {
  const { t, i18n } = useLingui();
  const { article, request, view, errors } = useArticleContext();

  const { mutateAsync: updateDefect } = useUpdateDefect({
    requestId: request.id,
    articleId: article.id,
  });

  const isEditable =
    !!defect &&
    view.defects.fields.brandResponsibility.exceptionalCoverage.toggle.editable &&
    !defect.id.startsWith(TEMPORARY_ID_PREFIX);

  const editedFields = getEditedFields({
    defect,
    previousDefect,
  });

  const isEdited =
    editedFields.isExceptionalBrandResponsibilityCoverageRequestedEdited ||
    editedFields.isExceptionalBrandResponsibilityCoverageReasonEdited;

  const labelType = getExceptionalCoverageRequestLabelType(article);
  const label =
    labelType === 'requested'
      ? t({
          id: 'defect-card.exceptional-coverage.request',
          message: 'Request exceptional brand coverage',
        })
      : t({
          id: 'defect-card.exceptional-coverage.covered-by-brand',
          message: 'Covered by brand',
        });

  const exceptionalCoverageReasonOptions = EXCEPTIONAL_BRAND_COVERAGE_REASONS.map((reason) => ({
    id: reason.id,
    text: i18n._(reason.text),
  }));

  const defectOrPreviousDefect = defect ?? previousDefect!;
  const selectedReasonOption = exceptionalCoverageReasonOptions.find(
    (option) => option.id === defectOrPreviousDefect.exceptionalBrandResponsibilityCoverageReason
  );
  const originalRequest =
    defectOrPreviousDefect.exceptionalBrandResponsibilityCoverageOriginalRequest;

  const exceptionalCoverageReasonLabel = t({
    id: 'defect-card.defect.exceptional-coverage.reason.label',
    message: 'Select a reason',
  });

  const missingExceptionalCoverageReason = !!errors.defects?.defects?.find(
    (defectError) => defect?.id === defectError.id
  )?.missingExceptionalCoverageReason;

  const wrapperStyles = {
    backgroundColor: isEdited ? 'var(--color-tertiary-100)' : undefined,
  };

  return (
    <div style={wrapperStyles}>
      <Stack gap="0.5rem">
        <Toggle
          label={label}
          alignment="left"
          isSelected={defectOrPreviousDefect.exceptionalBrandResponsibilityCoverageRequested}
          onChange={(value) => {
            if (defect) {
              updateDefect({
                defectId: defect.id,
                body: {
                  exceptionalBrandResponsibilityCoverageRequested: value,
                },
              });
            }
          }}
          isDisabled={!isEditable}
        />

        {defectOrPreviousDefect.exceptionalBrandResponsibilityCoverageRequested && (
          <>
            {isEditable && (
              <InputSelect
                aria-label={exceptionalCoverageReasonLabel}
                placeholder={exceptionalCoverageReasonLabel}
                options={exceptionalCoverageReasonOptions}
                value={selectedReasonOption}
                getOptionLabel={(option) => option.text}
                getOptionValue={(option) => option.id}
                onChange={(newValue) => {
                  if (defect) {
                    const reason = newValue?.id ?? null;

                    updateDefect({
                      defectId: defect.id,
                      body: {
                        exceptionalBrandResponsibilityCoverageReason: reason,
                      },
                    });
                  }
                }}
                error={
                  missingExceptionalCoverageReason
                    ? t({
                        id: 'defect-card.defect.exceptional-coverage.reason.error',
                        message: 'A reason is required',
                      })
                    : undefined
                }
              />
            )}
            {!isEditable && !!selectedReasonOption && (
              <Paragraph>
                <Trans id="defect-card.defect.exceptional-coverage.reason.reason">Reason:</Trans>{' '}
                {selectedReasonOption.text}
                {!!originalRequest && (
                  <>
                    {' • '}
                    <Trans id="defect-card.defect.exceptional-coverage.original-request.linked">
                      Linked request:
                    </Trans>{' '}
                    <LinkedOriginalRequestReference originalRequest={originalRequest} />
                  </>
                )}
              </Paragraph>
            )}
            {isEditable &&
              defectOrPreviousDefect.exceptionalBrandResponsibilityCoverageReason ===
                'covered-by-workshop-warranty' && (
                <OriginalRequestSearchSelect
                  articleId={article.id}
                  defect={defectOrPreviousDefect}
                  originalRequestId={
                    defectOrPreviousDefect.exceptionalBrandResponsibilityCoverageOriginalRequestId
                  }
                  onChange={(option) => {
                    if (defect) {
                      updateDefect({
                        defectId: defect.id,
                        body: {
                          exceptionalBrandResponsibilityCoverageOriginalRequestId:
                            option?.id ?? null,
                        },
                      });
                    }
                  }}
                />
              )}
          </>
        )}
      </Stack>
    </div>
  );
};
